import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, Renderer2 } from '@angular/core';
import { Subject, filter, takeUntil } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { RouterModule } from '@angular/router';
import { AuthserviceService } from '../../../core/services/authservice.service';
import { LoginResponse } from '../../../core/models/users';
import { NgxSpinnerService } from 'ngx-spinner';
import { NgxSpinnerModule } from 'ngx-spinner';
import { initFlowbite } from 'flowbite';
import { ClienteRequestsService } from '../../../core/services/cliente-requests.service';
import { ClienteBudgetsService } from '../../../core/services/cliente-budgets.service';
import { ChatUnreadSessionService } from '../../../core/services/chat-unread-session.service';

@Component({
    selector: 'app-sidenav',
    imports: [
        CommonModule,
        FormsModule,
        RouterModule,
        NgxSpinnerModule
    ],
    templateUrl: './sidenav.component.html',
    styleUrls: ['./sidenav.component.css']
})
export class SidenavComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() collapsed = false;
  @Output() closed = new EventEmitter<void>();

  isDarkMode = false;

  openSections = {
    solicitudes: true,
    presupuestos: true,
  };

  toggleSection(section: keyof typeof this.openSections) {
    this.openSections[section] = !this.openSections[section];
  }

  isSectionOpen(section: keyof typeof this.openSections): boolean {
    return this.openSections[section];
  }

  User: LoginResponse | undefined;
  readonly spinnerName = 'sidenavSpinner';
  pendientesCount = 0;
  hayIncidenciasAbiertas = false;
  presupuestosPendientesCount = 0;
  hayPresupuestosIncidenciasAbiertas = false;
  chatUnreadCount = 0;
  private readonly destroy$ = new Subject<void>();
  constructor(
    private router: Router,
    private authservice: AuthserviceService,
    private renderer: Renderer2,
    private spinner: NgxSpinnerService,
    private solicitudesService: ClienteRequestsService,
    private presupuestosService: ClienteBudgetsService,
    private chatUnreadSession: ChatUnreadSessionService
  ) {
    this.User = this.authservice.usuarioValue;
  }

  ngOnInit(): void {
    const stored = localStorage.getItem('dark-mode');
    this.isDarkMode = stored === 'true';
    this.updateBodyClass();
    this.cargarPendientes();
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.cargarPendientes());
    this.solicitudesService.pendientesRefresh$
      .pipe(takeUntil(this.destroy$))
      .subscribe((items) => {
        if (Array.isArray(items)) {
          this.aplicarResumenSolicitudes(items);
          return;
        }

        this.cargarPendientes();
      });
    this.presupuestosService.pendientesRefresh$
      .pipe(takeUntil(this.destroy$))
      .subscribe((items) => {
        if (Array.isArray(items)) {
          this.aplicarResumenPresupuestos(items);
          return;
        }

        this.cargarPendientesPresupuestos();
      });
    this.chatUnreadSession.unreadCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe((count) => this.chatUnreadCount = count);
  }

  ngAfterViewInit(): void {
    initFlowbite();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private cargarPendientes(): void {
    this.cargarPendientesSolicitudes();
    this.cargarPendientesPresupuestos();
  }

  private cargarPendientesSolicitudes(): void {
    this.solicitudesService.obtenerListadoSolicitudes()
      .subscribe({
        next: (response) => {
          const items = (response as any)?.items ?? (response as any)?.datos ?? [];
          this.aplicarResumenSolicitudes(items);
        },
        error: () => {
          this.pendientesCount = 0;
          this.hayIncidenciasAbiertas = false;
        }
      });
  }

  private cargarPendientesPresupuestos(): void {
    this.presupuestosService.obtenerListadoPresupuestos()
      .subscribe({
        next: (response) => {
          const items = (response as any)?.items ?? (response as any)?.datos ?? [];
          this.aplicarResumenPresupuestos(items);
        },
        error: () => {
          this.presupuestosPendientesCount = 0;
          this.hayPresupuestosIncidenciasAbiertas = false;
        }
      });
  }

  private aplicarResumenSolicitudes(items: any): void {
    if (!Array.isArray(items)) {
      this.pendientesCount = 0;
      this.hayIncidenciasAbiertas = false;
      return;
    }

    this.pendientesCount = items.filter((item) => {
      const estado = (item?.estado ?? '').toUpperCase();
      return estado === 'PENDIENTE';
    }).length;

    this.hayIncidenciasAbiertas = items.some((item) => {
      const estado = (item?.estado ?? '').toUpperCase();
      return estado === 'INCIDENCIA';
    });
  }

  private aplicarResumenPresupuestos(items: any): void {
    if (!Array.isArray(items)) {
      this.presupuestosPendientesCount = 0;
      this.hayPresupuestosIncidenciasAbiertas = false;
      return;
    }

    this.presupuestosPendientesCount = items.filter((item) => {
      const estado = (item?.estado ?? '').toUpperCase();
      return estado === 'PENDIENTE';
    }).length;

    this.hayPresupuestosIncidenciasAbiertas = items.some((item) => {
      const estado = (item?.estado ?? '').toUpperCase();
      return estado === 'INCIDENCIA';
    });
  }

  navigateTo(route: string): void {
    this.spinner.show(this.spinnerName);
    this.router.navigate([route])
      .finally(() => this.spinner.hide(this.spinnerName));
  }

  closeSidenav(): void {
    this.closed.emit();
  }

  closeMobileSidenav(): void {
    if (typeof window === 'undefined') return;
    if (!window.matchMedia('(max-width: 900px)').matches) return;
    this.closeSidenav();
  }

  logout() {
    this.spinner.show(this.spinnerName);
    this.authservice.logout();
    setTimeout(() => this.spinner.hide(this.spinnerName), 1500);
  }

  onDarkModeChange(value: boolean) {
    this.isDarkMode = value;
    localStorage.setItem('dark-mode', String(value));
    this.updateBodyClass();
  }

  private updateBodyClass() {
    if (this.isDarkMode) {
      this.renderer.addClass(document.body, 'dark');
    } else {
      this.renderer.removeClass(document.body, 'dark');
    }
  }
}

